import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest} from "@angular/common/http";
import {inject} from "@angular/core";
import {environment} from "environments/environment";
import {Observable, catchError, from, switchMap, throwError} from "rxjs";
import { AUTH_DAVESA_API } from "../auth-davesa/auth-davesa-api.component";
import { AuthDavesaV2Service } from "../auth-davesa/auth-davesa-v2.service";
import { UNSAFE_HTTP_METHODS } from "../constants/http.constants";

// Only send the session cookie to first-party origins. Third-party origins
// (Google Places, Google Cloud Storage, etc.) must never receive it.
const allowedOrigins = [
    environment.API_URL,
    environment.GLOBAL_SOCKET_URL,
    environment.SUBJECT_VISIT_SOCKET_URL,
    environment.STUDY_ESOURCE_SOCKET_URL,
];

/**
 * Fallback interceptor for requests that don't go through
 * auth-davesa-api.interceptor.ts (no AUTH_DAVESA_API context). The session
 * is an HttpOnly cookie now, so there's no token to attach - this just has
 * to make sure the cookie is actually sent.
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    if (!req.context.get(AUTH_DAVESA_API)) {
        const authDavesaV2Service = inject(AuthDavesaV2Service);

        const isAllowed = allowedOrigins.some((o) => o && req.url.startsWith(o));
        const isUnsafe = UNSAFE_HTTP_METHODS.includes(req.method);
        const build = () => {
            if (!isAllowed) return req;
            const csrfToken = authDavesaV2Service.csrfToken();
            return req.clone({
                withCredentials: true,
                ...(csrfToken && isUnsafe ? { setHeaders: { "X-CSRF-Token": csrfToken } } : {}),
            });
        };
        // After a reload the token is gone from memory; wait for the shared re-fetch.
        const waitForToken$ =
            isAllowed && isUnsafe && authDavesaV2Service.isLoggedIn() && !authDavesaV2Service.csrfToken()
                ? from(authDavesaV2Service.ensureCsrfToken())
                : null;

        return (waitForToken$ ? waitForToken$.pipe(switchMap(() => next(build()))) : next(build())).pipe(
            catchError((error) => {
                // A 401 from a third-party origin says nothing about our session.
                if (isAllowed && error instanceof HttpErrorResponse && error.status === 401) {
                    // Reload only after sign-out has cleared storage, or the
                    // reloaded app can restore the stale "logged in" state.
                    authDavesaV2Service.signOut().finally(() => location.reload());
                }

                return throwError(() => error);
            })
        );
    } else {
        return next(req);
    }
};
